import type {
  CallOffHistoryQueryParams,
  CallOffHistoryResponse,
} from './types';

import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodCallOffHistoryResponseSchema } from './validations';

/**
 * Fetches the version history for a call-off.
 *
 * @param callOffId - The UUID of the call-off.
 * @param queryParams - Pagination parameters (limit, offset).
 * @param options - Additional request options.
 * @returns The paginated history entries.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/get_history_calloffs__calloff_id__history_get
 */
export const fetchCallOffHistory = async (
  callOffId: UUID,
  queryParams?: CallOffHistoryQueryParams,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.CALL_OFF.GET_HISTORY(callOffId), {
      searchParams: queryParams,
      ...options,
    })
    .json<CallOffHistoryResponse>();

  const validatedResponse = zodCallOffHistoryResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'CallOff History API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
